import * as Sentry from '@sentry/browser';
import { getRandomSample, commonBeforeBreadcrumb, commonBeforeSentry, createSentryErrorDetail, SENTRY_COMMON_NOISE_FILTER } from '@encarpkg/shared';
import dayjs from 'app/libs/dayjs';

/**
 * Sentry 초기화 설정
 */
export const initSentry = () => {
	const beforeSend = (event, hint) => {
		const customEvent = commonBeforeSentry(event, hint, {
			apiUrl: process.env.REACT_APP_API_URL,
		});

		if (!customEvent) return null;

		// 외부사이트 iframe으로 fem페이지 사용시 발생하는 에러
		const errorMessage = event.exception?.values?.[0]?.value || event.message || '';
		const hasIframeBlockedError = errorMessage.includes(
			"Failed to read a named property 'document' from 'Window': Blocked a frame with origin \"https://fem.encar.com\" from accessing a cross-origin frame.",
		);

		if (hasIframeBlockedError) {
			if (getRandomSample(0.9)) return null;
			customEvent.tags = { ...customEvent.tags, sampled: '10%' };
		}
		return customEvent;
	};

	Sentry.init({
		dsn: process.env.REACT_APP_SENTRY_DSN,
		beforeBreadcrumb(breadcrumb) {
			return commonBeforeBreadcrumb(breadcrumb);
		},
		environment: process.env.REACT_APP_DEPLOY_ENV,
		release: process.env.REACT_APP_VERSION,
		integrations: [
			Sentry.replayIntegration({
				maskAllText: true,
				blockAllMedia: true,
				beforeErrorSampling(event) {
					const exception = event.exception?.values?.[0];
					const errorMessage = exception?.value || event.message || '';
					const errorType = exception?.type || '';

					const isBeforeEndDate = dayjs().isSameOrBefore('2026-01-22', 'day');

					if (
						errorType === 'TypeError' &&
						errorMessage.includes("null is not an object (evaluating 'document.getElementById('address').focus')") &&
						isBeforeEndDate
					) {
						return true;
					}

					return false;
				},
			}),
		],
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 1.0,
		tracesSampleRate: 1.0,
		beforeSend,
		ignoreErrors: [
			...SENTRY_COMMON_NOISE_FILTER.ignoreErrors,
			/TypeError: reduce of empty array with no initial value/i,
		],
		denyUrls: SENTRY_COMMON_NOISE_FILTER.denyUrls,
		beforeSendTransaction(event) {
			const lcp = event.measurements?.lcp?.value;
			if (!lcp) return null;
			// LCP 4초 이상인 정보만 수집
			if (lcp < 4000) return null;
			return event;
		},
	});
};

export const loggingData = (message, data = null) => {
	Sentry.withScope((scope) => {
		if (data) scope.setExtra('data', data);
		Sentry.captureMessage(message);
	});
};

export const loggingException = (error, { captureMessage } = { captureMessage: '' }) => {
	Sentry.withScope((scope) => {
		if (captureMessage) scope.setExtra('captureMessage', captureMessage);

		const { getContext, createError } = createSentryErrorDetail(error);

		scope.setContext('에러 컨텍스트', getContext());
		Sentry.captureException(createError());
	});
};

export default null;
